import React, { createContext, useState, useEffect, useContext } from 'react';
import { app, auth, database } from '../firebase.js'; 
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { ref, set, get, remove } from 'firebase/database';
import { getAI, GoogleAIBackend, getTemplateGenerativeModel } from "firebase/ai";

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [currentTab, setCurrentTab] = useState('player');
  const [workouts, setWorkouts] = useState(() => JSON.parse(localStorage.getItem('myWorkouts_v5')) || []);
  const [exerciseList, setExerciseList] = useState(() => JSON.parse(localStorage.getItem('myExerciseList_v5')) || ['벤치프레스', '스쿼트', '데드리프트']);
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [exerciseSelect, setExerciseSelect] = useState('');
  const [setsContainer, setSetsContainer] = useState([{ weight: '', reps: '' }]);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [calendarCurrentDate, setCalendarCurrentDate] = useState(new Date());
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [newExerciseName, setNewExerciseName] = useState('');
  const [jsonExchangeArea, setJsonExchangeArea] = useState('');
  const [authStatus, setAuthStatus] = useState(() => localStorage.getItem('logup_auth_status') || '');
  const [supplementsList, setSupplementsList] = useState([]);

  const [newCardioName, setNewCardioName] = useState('');
  const [newCardioUnit, setNewCardioUnit] = useState('거리');
  const [cardioList, setCardioList] = useState(() => JSON.parse(localStorage.getItem('myCardioList_v5')) || [{ name: '달리기', unit: '거리' }, { name: '자전거', unit: '거리' }]);
  const [cardioWorkouts, setCardioWorkouts] = useState(() => JSON.parse(localStorage.getItem('myCardioWorkouts_v5')) || []);
  const [cardioSelect, setCardioSelect] = useState('');
  const [cardioSetsContainer, setCardioSetsContainer] = useState([{ value: '' }]);
  const [cardioCurrentPage, setCardioCurrentPage] = useState(1);

  const [user, setUser] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [photoRecords, setPhotoRecords] = useState(() => JSON.parse(localStorage.getItem('myPhotoRecords_v5')) || []);
  const [playerData, setPlayerData] = useState({});

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setReportEndDate(end.toISOString().split('T')[0]);
    setReportStartDate(start.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (exerciseList.length > 0 && !exerciseSelect) setExerciseSelect(exerciseList[0]);
  }, [exerciseList, exerciseSelect]);

  useEffect(() => {
    if (cardioList.length > 0 && !cardioSelect) setCardioSelect(cardioList[0].name);
  }, [cardioList, cardioSelect]);

  const fetchPlayerData = async (currentMonth) => {
    if(!auth.currentUser) return;
    const date = new Date();
    const year = date.getFullYear();
    const month = currentMonth ?? date.getMonth()+1;
    const snap = await get(
      ref(
        database, 
        `users/${auth.currentUser.uid}/report/ai/${year}/${month}`
      )
    );
    const data = snap.val();
    setPlayerData(data);
  }

  const splitDateDash = (date) => {
    if (!date) {
      const now = new Date();
      return { yaer: String(now.getFullYear()), month: now.getMonth() + 1, day: String(now.getDate()).padStart(2, '0') };
    }
    const splitArr = date.split("-");
    return { yaer: splitArr[0], month: Number(splitArr[1]), day: splitArr[2] };
  };

  const flattenNestedData = (nestedData) => {
    if (!nestedData) return [];
    const result = [];
    Object.values(nestedData).forEach(yearData => {
      Object.values(yearData).forEach(monthData => {
        Object.values(monthData).forEach(dayData => {
          if (Array.isArray(dayData)) {
            result.push(...dayData);
          } else if (dayData && typeof dayData === 'object') {
            if (dayData.id) {
              result.push(dayData);
            } else {
              result.push(...Object.values(dayData));
            }
          }
        });
      });
    });
    return result;
  };

  const updateWorkouts = async (nextWorkouts, date) => {
    setWorkouts(nextWorkouts);
    if (!date) return;
    const pickDate = splitDateDash(date);
    if (auth.currentUser) await set(ref(database, `users/${auth.currentUser.uid}/workouts/${pickDate.yaer}/${pickDate.month}/${pickDate.day}`), nextWorkouts);
    else localStorage.setItem('myWorkouts_v5', JSON.stringify(nextWorkouts));
  };

  const updateCardioWorkouts = async (nextCardioWorkouts, date) => {
    setCardioWorkouts(nextCardioWorkouts);
    if (!date) return;
    const pickDate = splitDateDash(date);
    if (auth.currentUser) await set(ref(database, `users/${auth.currentUser.uid}/cardioWorkouts/${pickDate.yaer}/${pickDate.month}/${pickDate.day}`), nextCardioWorkouts);
    else localStorage.setItem('myCardioWorkouts_v5', JSON.stringify(nextCardioWorkouts));
  };

  const updateExerciseList = async (nextExerciseList) => {
    setExerciseList(nextExerciseList);
    if (auth.currentUser) await set(ref(database, `users/${auth.currentUser.uid}/exerciseList`), nextExerciseList);
    else localStorage.setItem('myExerciseList_v5', JSON.stringify(nextExerciseList));
  };

  const updateCardioList = async (nextCardioList) => {
    setCardioList(nextCardioList);
    if (auth.currentUser) await set(ref(database, `users/${auth.currentUser.uid}/cardioList`), nextCardioList);
    else localStorage.setItem('myCardioList_v5', JSON.stringify(nextCardioList));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        setAuthStatus('google');
        localStorage.setItem('logup_auth_status', 'google');
        const userRef = ref(database, `users/${currentUser.uid}`);
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.app !== 'logup') await set(ref(database, `users/${currentUser.uid}/app`), 'logup');
          
          const flatWorkouts = flattenNestedData(data.workouts);
          setWorkouts(flatWorkouts);
          
          const flatCardio = flattenNestedData(data.cardioWorkouts);
          setCardioWorkouts(flatCardio.length > 0 ? flatCardio : (data.cardioWorkouts || []));
          
          setExerciseList(data.exerciseList || ['벤치프레스', '스쿼트', '데드리프트']);
          setCardioList(data.cardioList || [{ name: '달리기', unit: '거리' }, { name: '자전거', unit: '거리' }]);
          
          const flatSupplements = flattenNestedData(data.supplements);
          flatSupplements.sort((a, b) => b.timestamp - a.timestamp);
          setSupplementsList(flatSupplements);

          await fetchPlayerData();
        } else {
          const localWorkouts = JSON.parse(localStorage.getItem('myWorkouts_v5')) || [];
          const localCardioWorkouts = JSON.parse(localStorage.getItem('myCardioWorkouts_v5')) || [];
          const localExerciseList = JSON.parse(localStorage.getItem('myExerciseList_v5')) || ['벤치프레스', '스쿼트', '데드리프트'];
          const localCardioList = JSON.parse(localStorage.getItem('myCardioList_v5')) || [{ name: '달리기', unit: '거리' }, { name: '자전거', unit: '거리' }];
          const localPhotoRecords = JSON.parse(localStorage.getItem('myPhotoRecords_v5')) || [];

          await set(userRef, { app: 'logup', workouts: localWorkouts, cardioWorkouts: localCardioWorkouts, exerciseList: localExerciseList, cardioList: localCardioList, photoRecords: localPhotoRecords });
          setWorkouts(localWorkouts); 
          setCardioWorkouts(localCardioWorkouts); 
          setExerciseList(localExerciseList); 
          setCardioList(localCardioList);
        }
      } else {
        const status = localStorage.getItem('logup_auth_status');
        setAuthStatus(status === 'guest' ? 'guest' : '');
        setWorkouts(JSON.parse(localStorage.getItem('myWorkouts_v5')) || []);
        setCardioWorkouts(JSON.parse(localStorage.getItem('myCardioWorkouts_v5')) || []);
        setExerciseList(JSON.parse(localStorage.getItem('myExerciseList_v5')) || ['벤치프레스', '스쿼트', '데드리프트']);
        setCardioList(JSON.parse(localStorage.getItem('myCardioList_v5')) || [{ name: '달리기', unit: '거리' }, { name: '자전거', unit: '거리' }]);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchSupplements = async userId => {
    const snapshot = await get(ref(database, `users/${userId}/supplements`));
    const flatSupplements = flattenNestedData(snapshot.val());
    flatSupplements.sort((a, b) => b.timestamp - a.timestamp);
    setSupplementsList(flatSupplements);
  };

  const handleSupplementSave = async ({supplementText, supplementDate}) => {
    try {
      const userId = auth.currentUser.uid;
      const id = Date.now();
      const pickDate = splitDateDash(supplementDate);
      await set(
        ref(database, `users/${userId}/supplements/${pickDate.yaer}/${pickDate.month}/${pickDate.day}`), 
        { id, text: supplementText.trim(), timestamp: Date.now(), app: "logup", date: supplementDate }
      );
      await fetchSupplements(userId);
    } catch (error) { alert('저장 중 오류가 발생했습니다.'); }
  };

  // --- 삭제 메서드 수정 영역 ---
  const deleteRecord = async (id) => {
    if (!window.confirm("삭제할까요?")) return;
    const target = workouts.find(w => w.id === id);
    if (!target) return;

    const nextWorkouts = workouts.filter(w => w.id !== id);
    setWorkouts(nextWorkouts);

    if (auth.currentUser) {
      const pickDate = splitDateDash(target.date);
      const sameDayWorkouts = nextWorkouts.filter(w => w.date === target.date);
      const dayRef = ref(database, `users/${auth.currentUser.uid}/workouts/${pickDate.yaer}/${pickDate.month}/${pickDate.day}`);
      
      if (sameDayWorkouts.length > 0) {
        await set(dayRef, sameDayWorkouts);
      } else {
        await remove(dayRef);
      }
    } else {
      localStorage.setItem('myWorkouts_v5', JSON.stringify(nextWorkouts));
    }
  };

  const deleteCardioRecord = async (id) => {
    if (!window.confirm("유산소 기록을 삭제할까요?")) return;
    const target = cardioWorkouts.find(c => c.id === id);
    if (!target) return;

    const nextCardioWorkouts = cardioWorkouts.filter(c => c.id !== id);
    setCardioWorkouts(nextCardioWorkouts);

    if (auth.currentUser) {
      const pickDate = splitDateDash(target.date);
      const sameDayCardio = nextCardioWorkouts.filter(c => c.date === target.date);
      const dayRef = ref(database, `users/${auth.currentUser.uid}/cardioWorkouts/${pickDate.yaer}/${pickDate.month}/${pickDate.day}`);
      
      if (sameDayCardio.length > 0) {
        await set(dayRef, sameDayCardio);
      } else {
        await remove(dayRef);
      }
    } else {
      localStorage.setItem('myCardioWorkouts_v5', JSON.stringify(nextCardioWorkouts));
    }
  };

  const deleteSupplements = async (id) => {
    if (!window.confirm("삭제할까요?")) return;
    const target = supplementsList.find(s => s.id === id);

    if (auth.currentUser) {
      if (target && target.date) {
        const pickDate = splitDateDash(target.date);
        await remove(ref(database, `users/${auth.currentUser.uid}/supplements/${pickDate.yaer}/${pickDate.month}/${pickDate.day}`));
      } else {
        await remove(ref(database, `users/${auth.currentUser.uid}/supplements/${id}`));
      }
      await fetchSupplements(auth.currentUser.uid);
    } else {
      const nextSupplements = supplementsList.filter(s => s.id !== id);
      setSupplementsList(nextSupplements);
    }
  };

  const saveAll = (draftBlocks, date) => {
    const newWorkouts = [], newCardioWorkouts = [];
    draftBlocks.forEach((block, index) => {
      const baseId = Date.now() + index;
      if (block.type === 'workout') {
        const parsedSets = block.sets
            .map(s => ({ 
                weight: parseFloat(s.weight), 
                reps: parseInt(s.reps),
                difficulty: s.difficulty || '보통'
            }))
            .filter(s => s.weight > 0 && s.reps > 0);

        if (block.name && parsedSets.length > 0) {
            newWorkouts.push({ id: baseId, date, name: block.name, sets: parsedSets });
        }

      } else if (block.type === 'cardio') {
        const parsedSets = block.sets.map(s => {
          const p = { phase: s.phase || '본 운동' };
          
          if (parseFloat(s.time) > 0) p.time = parseFloat(s.time);
          if (parseFloat(s.distance) > 0) p.distance = parseFloat(s.distance);
          if (parseFloat(s.reps) > 0) p.reps = parseFloat(s.reps);
          if (parseFloat(s.speed) > 0) p.speed = parseFloat(s.speed);
          if (s.incline !== undefined && s.incline !== '' && !isNaN(parseFloat(s.incline))) p.incline = parseFloat(s.incline);
          if (s.pace && s.pace.trim() !== '') p.pace = s.pace.trim();
          return p;
        }).filter(s => Object.keys(s).length > 1);
        if (block.name && parsedSets.length > 0) newCardioWorkouts.push({ id: baseId, date, name: block.name, sets: parsedSets });
      }
    });
    if (newWorkouts.length > 0) updateWorkouts([...newWorkouts], date);
    if (newCardioWorkouts.length > 0) updateCardioWorkouts([...newCardioWorkouts], date);
  };

  const validateRange = (startVal, endVal) => {
    const startDate = new Date(startVal), endDate = new Date(endVal);
    if (startDate > endDate) { alert("시작일이 종료일보다 늦을 수 없습니다."); setReportStartDate(endVal); return; }
    if (Math.ceil(Math.abs(endDate - startDate) / (1000 * 60 * 60 * 24)) > 10) {
      alert("최대 10일까지만 조회가 가능합니다.");
      const maxDate = new Date(startDate); maxDate.setDate(maxDate.getDate() + 10);
      setReportEndDate(maxDate.toISOString().split('T')[0]);
    }
  };

  const getRecentMaxText = workout_name => {
    const target = workout_name;
    if (!target) return "선택된 운동 종목 없음";
    const filtered = workouts.filter(w => w.name === target).sort((a, b) => new Date(b.date) - new Date(a.date));
    if (filtered.length === 0) return "최근 기록 없음";
    const recentRecord = filtered[0];
    let maxWeight = 0;
    let maxReps = 0;
    
    recentRecord.sets.forEach(s => {
      const weight = parseFloat(s.weight) || 0;
      const reps = parseInt(s.reps) || 0;
      if (weight >= maxWeight) {
        maxWeight = weight;
        maxReps = reps;
      }
    });
    const dParts = recentRecord.date.split('-');
    return (
      <>
        <b className="text-indigo-600 font-extrabold">{maxWeight} kg × {maxReps}회</b> 
        <span className="text-[10px] text-slate-400">
          ({dParts[1] ? `${dParts[1]}/${dParts[2]}` : recentRecord.date})
        </span>
      </>
    );
  };

  const handleSetFieldChange = (index, field, value) => { const nextSets = [...setsContainer]; nextSets[index][field] = value; setSetsContainer(nextSets); };
  const addSetRow = () => setSetsContainer([...setsContainer, { weight: '', reps: '' }]);
  
  const saveWorkout = () => {
    const name = exerciseSelect || exerciseList[0];
    const parsedSets = setsContainer.map(row => ({ weight: parseFloat(row.weight), reps: parseInt(row.reps) })).filter(s => s.weight > 0 && s.reps > 0);
    if (!name || parsedSets.length === 0) { alert("데이터를 입력하세요."); return; }
    updateWorkouts([{ id: Date.now(), date: workDate, name, sets: parsedSets }, ...workouts], workDate);
    setSetsContainer([{ weight: '', reps: '' }]); setCurrentPage(1); alert("저장 완료!");
  };

  const handleCardioSetFieldChange = (index, field, value) => { const nextSets = [...cardioSetsContainer]; nextSets[index][field] = value; setCardioSetsContainer(nextSets); };
  const addCardioSetRow = () => setCardioSetsContainer([...cardioSetsContainer, { value: '' }]);

  const addExerciseLabel = () => { if (newExerciseName.trim() && !exerciseList.includes(newExerciseName.trim())) { updateExerciseList([...exerciseList, newExerciseName.trim()]); setNewExerciseName(''); } };
  const removeExercise = n => { if (window.confirm("제거할까요?")) { updateExerciseList(exerciseList.filter(e => e !== n)); if (exerciseSelect === n) setExerciseSelect(''); } };

  const addCardioLabel = _ => {
    const trimmedName = newCardioName.trim();
    const isDuplicate = cardioList.some(c => c.name === trimmedName);
    if (trimmedName && !isDuplicate) {
      updateCardioList([...cardioList, { name: trimmedName, unit: newCardioUnit }]);
      setNewCardioName('');
      setNewCardioUnit('거리'); 
    } else if (isDuplicate) {
      alert("이미 등록된 유산소 종목입니다.");
    }
  };
  
  const removeCardio = nameToRemove => {
    if (window.confirm("유산소 종목을 제거할까요?")) {
      updateCardioList(cardioList.filter(c => c.name !== nameToRemove));
      if (cardioSelect === nameToRemove) setCardioSelect('');
    }
  };

  const handleLoginSelect = status => { localStorage.setItem('logup_auth_status', status); setAuthStatus(status); };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      alert('구글 계정으로 로그인되었습니다.');
    } catch (error) {
      console.error(error);
      alert(`구글 로그인 실패: ${error.message}`);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('logup_auth_status');
      setAuthStatus('');
      alert('로그아웃되었습니다.');
    } catch (error) {
      alert('로그아웃 실패');
    }
  };

  const backupToCloud = async () => {
    if (!user) {
      alert('로그인이 필요한 기능입니다.');
      return;
    }
    try {
      setSyncMessage('데이터 백업 중...');
      
      const localHistory = localStorage.getItem('myWorkouts_v5') || '[]';
      const currentExercises = exerciseList && exerciseList.length > 0 
        ? exerciseList 
        : JSON.parse(localStorage.getItem('exercise_list') || '[]');

      await set(ref(database, `users/${user.uid}`), {
        workout: JSON.parse(localHistory),
        exerciseList: currentExercises,
        lastSynced: new Date().toISOString()
      });

      setSyncMessage('클라우드 백업 완료!');
      setTimeout(() => setSyncMessage(''), 3000);
    } catch (error) {
      console.error(error);
      setSyncMessage('백업 실패. 기기 설정을 확인하세요.');
    }
  };

  const fetchFromCloud = async () => {
    if (!user) {
      alert('로그인이 필요한 기능입니다.');
      return;
    }
    if (!window.confirm('클라우드 데이터를 불러오면 현재 기기의 데이터가 모두 덮어씌워집니다. 진행하시겠습니까?')) return;

    try {
      setSyncMessage('데이터 불러오는 중...');
      const snapshot = await get(ref(database, `users/${user.uid}`));
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        if (data.workout) {
          localStorage.setItem('myWorkouts_v5', JSON.stringify(data.workout));
          setWorkouts(data.workout);
        }
        
        if (data.photoRecords) {
          localStorage.setItem('photo_records', JSON.stringify(data.photoRecords));
          setPhotoRecords(data.photoRecords);
        }
        
        if (data.exerciseList) {
          localStorage.setItem('exercise_list', JSON.stringify(data.exerciseList));
          setExerciseList(data.exerciseList);
        }
        
        setSyncMessage('데이터 동기화 완료! 안전하게 반영하기 위해 새로고침을 실행합니다.');
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setSyncMessage('저장된 클라우드 데이터가 없습니다.');
        setTimeout(() => setSyncMessage(''), 3000);
      }
    } catch (error) {
      console.error(error);
      setSyncMessage('데이터 불러오기 실패');
    }
  };

  const renderCalendarDays = () => {
    const year = calendarCurrentDate.getFullYear(), month = calendarCurrentDate.getMonth();
    const firstDayIndex = new Date(year, month, 1).getDay(), lastDate = new Date(year, month + 1, 0).getDate();
    const items = [];
    for (let i = 0; i < firstDayIndex; i++) items.push(<div key={`b-${i}`}></div>);
    for (let day = 1; day <= lastDate; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const currentWeekIndex = (firstDayIndex + day - 1) % 7;
      let clr = "text-slate-700"; if (currentWeekIndex === 0) clr = "text-rose-500"; if (currentWeekIndex === 6) clr = "text-blue-500";
      
      const hasWorkout = workouts.some(w => w.date === dateStr);
      const hasCardio = cardioWorkouts.some(c => c.date === dateStr);

      items.push(
        <div key={`d-${day}`} className="flex flex-col items-center justify-center py-1 relative">
          <span className={clr}>{day}</span>
          <div className="flex gap-0.5 absolute bottom-[-4px]">
            {hasWorkout && <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>}
            {hasCardio && <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>}
          </div>
        </div>
      );
    }
    return items;
  };

  const moveCalendarMonth = v => setCalendarCurrentDate(
    new Date(calendarCurrentDate.setMonth(calendarCurrentDate.getMonth() + v))
  )

  const draftSaveKey = "logup_temp_draft";
  
  const saveTempDraft = (draftBlocks, currentDate) => {
    if (!draftBlocks || draftBlocks.length === 0) {
        localStorage.removeItem(draftSaveKey);
        return;
    }
    localStorage.setItem(draftSaveKey, JSON.stringify({ date: currentDate, draftBlocks }));
  };

  const loadTempDraft = currentDate => {
    const stored = localStorage.getItem(draftSaveKey);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      if (parsed.date !== currentDate) {
        localStorage.removeItem(draftSaveKey);
        return null;
      }
      const drafts = parsed.draftBlocks || [];
      
      let nextExerciseList = [...exerciseList];
      let nextCardioList = [...cardioList];
      let isExerciseUpdated = false;
      let isCardioUpdated = false;

      drafts.forEach(block => {
        if (block.type === 'workout' && block.name && !nextExerciseList.includes(block.name)) {
          nextExerciseList.push(block.name);
          isExerciseUpdated = true;
        } else if (block.type === 'cardio' && block.name && !nextCardioList.some(c => c.name === block.name)) {
          nextCardioList.push({ name: block.name, unit: '거리' });
          isCardioUpdated = true;
        }
      });
      
      if (isExerciseUpdated) updateExerciseList(nextExerciseList);
      if (isCardioUpdated) updateCardioList(nextCardioList);

      return drafts;
    } catch (error) {
      localStorage.removeItem(draftSaveKey);
      return null;
    }
  };
  const clearTempDraft = _ => localStorage.removeItem(draftSaveKey);

  const getRecentWorkoutData = async _ => {
    const date = new Date(Date.now());
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate() - 1;
    const snap = await get(
      ref(
        database, 
        `users/${auth.currentUser.uid}/workouts/${year}/${month}/${day}`
      )
    );
    return snap.val();
  }

  async function geminiAiPromptRun({input, recent, date}) {
    try{
      const ai = getAI(app, { backend: new GoogleAIBackend() });
      const model = getTemplateGenerativeModel(ai);
      const modelTemplateId = 'logup-report-v2';
      const result = await model.generateContent(
        modelTemplateId,
        {input, recent}
      );
      const resultReport = result.response.text();
      const reportParse = saveUserAiReport({reportTxt: resultReport, date});
      return reportParse;
    }
    catch(e){
      console.log(`e ${e}`);
      return "error";
    }
  }

  const saveUserAiReport = async ({reportTxt, date}) => {
    const parser = JSON.parse(reportTxt);
    const {common, oneline, rank, next} = parser;
    if (auth.currentUser){
      const year = date.getFullYear();
      const month = date.getMonth()+1;
      const day = date.getDate();
      const parseDate = `${year}-${month}-${day}`;
      await set(
        ref(
          database, 
          `users/${auth.currentUser.uid}/report/ai/${year}/${month}/${day}`
        ), 
        {common, oneline, rank, next, date:parseDate}
      );
      await fetchPlayerData();
    }
    const resultReport = `${common}\n\n**총평** ${oneline}\n\n**평가**  ${rank}점\n\n${next}`;
    return resultReport;
  }
  
  const parseAIReport = text => {
    if (!text) return <></>;
    return (
        <div className="whitespace-pre-wrap text-sm text-slate-700 space-y-1">
            {text.split('\n').map((line, lineIndex) => {
                if (line.trim() === '') return <div key={lineIndex} className="h-2"></div>;
                const parts = line.split(/(\*\*.*?\*\*)/g);
                return (
                  <div key={lineIndex} className="leading-relaxed">
                      {parts.map((part, partIndex) => {
                          if (part.startsWith('**') && part.endsWith('**')) return (
                              <strong key={partIndex} className="font-black text-slate-900">
                                {part.slice(2, -2)}
                              </strong>
                            );
                          return <span key={partIndex}>{part}</span>;
                      })}
                  </div>
                );
            })}
        </div>
    );
  }

  const value = {
    currentTab, setCurrentTab, workouts, setWorkouts, 
    exerciseList, setExerciseList, workDate, setWorkDate,
    exerciseSelect, setExerciseSelect, setsContainer, setSetsContainer, 
    reportStartDate, setReportStartDate, reportEndDate, setReportEndDate, 
    calendarCurrentDate, setCalendarCurrentDate, currentPage, setCurrentPage,
    itemsPerPage, newExerciseName, setNewExerciseName, jsonExchangeArea, setJsonExchangeArea, 
    authStatus, setAuthStatus, supplementsList, setSupplementsList, 
    newCardioName, setNewCardioName, newCardioUnit, setNewCardioUnit, 
    cardioList, setCardioList, cardioWorkouts, setCardioWorkouts, 
    cardioSelect, setCardioSelect, cardioSetsContainer, setCardioSetsContainer, 
    cardioCurrentPage, setCardioCurrentPage, photoRecords, setPhotoRecords,
    user, syncMessage,
    updateWorkouts, updateCardioWorkouts, updateExerciseList, updateCardioList, fetchSupplements,
    handleSupplementSave, validateRange, getRecentMaxText, handleSetFieldChange, addSetRow, saveWorkout,
    deleteRecord, handleCardioSetFieldChange, addCardioSetRow, deleteCardioRecord,
    deleteSupplements, addExerciseLabel, removeExercise, addCardioLabel, removeCardio,
    handleLoginSelect, handleGoogleLogin, handleLogout, saveAll,
    backupToCloud, fetchFromCloud,
    renderCalendarDays, moveCalendarMonth, saveTempDraft, loadTempDraft, clearTempDraft,
    geminiAiPromptRun, parseAIReport, getRecentWorkoutData,
    playerData, setPlayerData
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);